import axios from 'axios';

// Environment-based API URL configuration
export const getApiUrl = () => {
  // Check if we have an environment variable set
  const envApiUrl = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
  
  if (envApiUrl) {
    return envApiUrl;
  }
  
  // Fallback based on environment
  const isDev = Boolean((import.meta as any)?.env?.DEV);
  if (isDev) {
    // Development: use local Strapi
    return 'http://localhost:1337/api';
  } else {
    // Production: use Strapi Cloud URL
    return 'https://elegant-advice-80456e5961.strapiapp.com/api';
  }
};

const API_BASE_URL = getApiUrl();

// Log API configuration in development only
if ((import.meta as any)?.env?.DEV) {
  console.log('🌐 API Base URL:', API_BASE_URL);
  console.log('🏗️ Environment: Development');
}

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 20000, // Increase timeout to 20 seconds to account for cold starts / heavy populate
});

// Simple retry with exponential backoff for transient errors/timeouts
const MAX_RETRIES = 2;
const BASE_DELAY_MS = 500;
const CACHE_TTL_SUCCESS_MS = 10 * 60 * 1000; // 10 minutes
const CACHE_MAX_STALE_MS = 60 * 60 * 1000; // 1 hour stale allowed on errors

type CacheEntry = {
  timestamp: number;
  data: unknown;
};

function makeCacheKey(config: any): string {
  const url = (config?.baseURL ? config.baseURL.replace(/\/$/, '') : '') + (config?.url || '');
  const params = config?.params ? `?${JSON.stringify(config.params)}` : '';
  return `api-cache:${url}${params}`;
}

api.interceptors.response.use(
  (response) => {
    try {
      // Cache only successful GET responses
      const method = (response?.config?.method || 'get').toLowerCase();
      const skipCache = Boolean(
        (response?.config as any)?.params?.noCache ||
          (response?.config as any)?.headers?.['x-skip-cache']
      );
      if (method === 'get' && !skipCache && typeof window !== 'undefined' && window?.localStorage) {
        const key = makeCacheKey(response.config);
        const entry: CacheEntry = { timestamp: Date.now(), data: response.data };
        window.localStorage.setItem(key, JSON.stringify(entry));
      }
    } catch (_) {
      // ignore cache failures
    }
    return response;
  },
  async (error) => {
    const config: any = error.config || {};
    const status = error?.response?.status;
    const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
    const isTransient = !status || status >= 500; // network error or 5xx
    const skipCache = Boolean(config?.params?.noCache || config?.headers?.['x-skip-cache']);

    config.__retryCount = config.__retryCount || 0;

    if ((isTimeout || isTransient) && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      const delay = BASE_DELAY_MS * Math.pow(2, config.__retryCount - 1) + Math.random() * 200;
      await new Promise((res) => setTimeout(res, delay));
      return api.request(config);
    }

    // Attempt to serve stale cache on error for GET requests (unless explicitly skipped)
    try {
      const method = (config?.method || 'get').toLowerCase();
      if (method === 'get' && !skipCache && typeof window !== 'undefined' && window?.localStorage) {
        const key = makeCacheKey(config);
        const raw = window.localStorage.getItem(key);
        if (raw) {
          const entry = JSON.parse(raw) as CacheEntry;
          if (Date.now() - entry.timestamp <= CACHE_MAX_STALE_MS) {
            return Promise.resolve({
              data: entry.data,
              status: 200,
              statusText: 'OK (cache)'.toString(),
              headers: {},
              config,
              request: undefined,
            });
          }
        }
      }
    } catch (_) {
      // ignore cache failures
    }

    return Promise.reject(error);
  }
);

// Locale helper: determine current locale (default nl)
export function getCurrentLocale(): 'nl' | 'ar' {
  // 1) Query params override everything (supports lang or locale; also handles ar-XX / nl-XX)
  try {
    const params = new URLSearchParams(window.location.search);
    const qpRaw = params.get('lang') || params.get('locale');
    if (qpRaw) {
      const qp = qpRaw.toLowerCase();
      const selected = qp.startsWith('ar') ? 'ar' : 'nl';
      try { localStorage.setItem('hm_locale', selected); } catch (_) {}
      return selected;
    }
  } catch (_) {}

  // 2) Use URL path prefix if present (/ar/... or /nl/...)
  try {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const prefix = (segments[0] || '').toLowerCase();
    if (prefix.startsWith('ar')) return 'ar';
    if (prefix.startsWith('nl')) return 'nl';
  } catch (_) {}

  // 3) Stored preference
  try {
    const stored = (localStorage.getItem('hm_locale') || '').toLowerCase();
    if (stored === 'ar') return 'ar';
  } catch (_) {}

  // 4) Default
  return 'nl';
}

// Inject locale param automatically
api.interceptors.request.use((config) => {
  const locale = getCurrentLocale();
  if (!config.params) config.params = {};
  if (!('locale' in (config.params as any))) {
    (config.params as any).locale = locale;
  }
  return config;
});

// Helper function to get Strapi data with proper formatting
const getStrapiData = (response: any) => {
  // Handle Strapi Cloud structure: response.data.data contains the actual content
  if (response.data && response.data.data) {
    if (Array.isArray(response.data.data)) {
      // Collection endpoint
      return response.data.data;
    } else {
      // Single type endpoint - return the data object directly
      return response.data.data;
    }
  }
  
  // Handle case where data is directly in response.data (older structure)
  if (response.data && response.data.id) {
    return response.data;
  }
  
  // Handle case where data is an array (e.g., /users-permissions/roles)
  if(Array.isArray(response.data)) {
    return response.data.map((item: any) => (
      {id: item.id, ...item}
    ));
  }
  
  return response.data;
};

// API service functions
export const apiService = {
  // Get hero section data
  getHeroSection: async () => {
    try {
      const response = await api.get('/hero-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching hero section:', error);
      throw error;
    }
  },

  // Get about section data (over-ons-content) - DEPRECATED: Use getLocationAboutContent instead
  getAboutSection: async () => {
    try {
      const locale = getCurrentLocale();
      const response = await api.get('/location-about-content', {
        params: { populate: '*', locale }
      });
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching location-about-content (deprecated endpoint):', error);
      throw error;
    }
  },

  // Get CTA section data
  getCTASection: async () => {
    try {
      const response = await api.get('/cta-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching CTA section:', error);
      throw error;
    }
  },

  // Get header section data
  getHeaderSection: async () => {
    try {
      const response = await api.get('/header-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching header section:', error);
      throw error;
    }
  },

      // Get behandelingen approach section data - UPDATED VERSION v15.0 with step images
    getApproachSection: async () => {
      try {
        console.log('🔍 ApproachSection API: Fetching behandelingen-approach data with step images');
        const response = await api.get('/behandelingen-approach?populate=image&populate=steps&populate=steps.image');
        return getStrapiData(response);
      } catch (error) {
        console.error('Error fetching behandelingen-approach:', error);
        throw error;
      }
    },



  // Get global settings
  getGlobalSettings: async () => {
    try {
      const response = await api.get('/global-settings', {
        params: {
          populate: 'logo,seo',
        },
      } as any);
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching global settings:', error);
      throw error;
    }
  },

  // Get treatment zones
  getTreatmentZones: async () => {
    try {
      const response = await api.get('/treatment-zones?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching treatment zones:', error);
      throw error;
    }
  },

  // Get algemene voorwaarden
  getAlgemeneVoorwaarden: async () => {
    try {
      const response = await api.get('/legal-terms?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching algemene voorwaarden:', error);
      throw error;
    }
  },

  // Get privacy policy
  getPrivacyPolicy: async () => {
    try {
      const response = await api.get('/privacy-policy?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      throw error;
    }
  },

  // Get three steps approach
  getThreeStepsApproach: async () => {
    try {
      const response = await api.get('/3stepsapproach?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching three steps approach:', error);
      throw error;
    }
  },

  // Get behandelingen approach section
  getBehandelingenApproach: async () => {
    try {
      const response = await api.get('/behandelingen-approach?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching behandelingen approach:', error);
      throw error;
    }
  },

  // Get before after slider images
  getBeforeAfterSlider: async () => {
    try {
      const response = await api.get('/before-after-sliders?populate=*&sort=order:asc');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching before after slider:', error);
      throw error;
    }
  },

  // Get expectations section
  getExpectationsSection: async () => {
    try {
      const response = await api.get('/expectations-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching expectations section:', error);
      throw error;
    }
  },

  // Get FAQ section
  getFaqSection: async () => {
    try {
      const response = await api.get('/faq-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching FAQ section:', error);
      throw error;
    }
  },

  // Get clinic photos section
  getClinicPhotosSection: async () => {
    try {
      const response = await api.get('/clinic-photos-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching clinic photos section:', error);
      throw error;
    }
  },

  // Get about section data (about-section content type)
  getAboutSectionContent: async () => {
    try {
      const response = await api.get('/about-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching about-section:', error);
      throw error;
    }
  },

  // Get over-ons hero section data  
  getOverOnsHero: async () => {
    try {
      const response = await api.get('/over-ons-hero?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching over-ons-hero:', error);
      throw error;
    }
  },

  // Get location about content (location section)
  getLocationAboutContent: async () => {
    try {
      const locale = getCurrentLocale();
      const response = await api.get('/location-about-content', {
        params: { populate: '*', locale }
      });
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching location-about-content:', error);
      throw error;
    }
  },

  // DEPRECATED: Use getLocationAboutContent instead
  getOverOnsContent: async () => {
    try {
      const response = await api.get('/location-about-content?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching location-about-content:', error);
      throw error;
    }
  },

  // Get USP section data
  getUspSection: async () => {
    try {
      const response = await api.get('/usp-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching usp-section:', error);
      throw error;
    }
  },

  // Get behandelingen intro section data
  getBehandelingenIntro: async () => {
    try {
      const response = await api.get('/behandelingen-intro?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching behandelingen-intro:', error);
      throw error;
    }
  },

  // Get why choose section data
  getWhyChooseSection: async () => {
    try {
      const response = await api.get('/why-choose-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching why-choose-section:', error);
      throw error;
    }
  },

  // Get team section data with team members
  getTeamSection: async () => {
    try {
      const response = await api.get('/team-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching team-section:', error);
      throw error;
    }
  },

  // Get team members collection
  getTeamMembers: async () => {
    try {
      const response = await api.get('/team-members?populate=image&sort=order:asc');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching team-members:', error);
      throw error;
    }
  },

  // Get contact section data
  getContactSection: async () => {
    try {
      const response = await api.get('/contact-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching contact-section:', error);
      throw error;
    }
  },

  // Get footer section data
  getFooterSection: async () => {
    try {
      const response = await api.get('/footer-section?populate=*');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching footer-section:', error);
      throw error;
    }
  },

  // Get contact form copy
  getContactForm: async () => {
    try {
      const response = await api.get('/contact-form');
      return getStrapiData(response);
    } catch (error) {
      console.error('Error fetching contact form:', error);
      throw error;
    }
  },

  // Get behandelingen hero section data
  getBehandelingenHero: async () => {
    try {
      console.log('🌐 getBehandelingenHero - Making API call...');
      const response = await api.get('/behandelingen-hero?populate=*');
      console.log('🌐 getBehandelingenHero - Raw response:', response.data);
      const result = getStrapiData(response);
      console.log('🌐 getBehandelingenHero - Processed result:', result);
      return result;
    } catch (error) {
      console.error('Error fetching behandelingen-hero:', error);
      throw error;
    }
  },

  // Get contact image module data
  getContactImageModule: async () => {
    try {
      console.log('🌐 getContactImageModule - Making API call...');
      const response = await api.get('/contact-image-module?populate=*');
      console.log('🌐 getContactImageModule - Raw response:', response.data);
      const result = getStrapiData(response);
      console.log('🌐 getContactImageModule - Processed result:', result);
      return result;
    } catch (error) {
      console.error('Error fetching contact-image-module:', error);
      throw error;
    }
  },
};

export default apiService;